'use client'
import { cubicBezier } from 'motion'
import { motion } from 'motion/react'
import type { FC } from 'react'
import { IconComponent } from '@/components/Icon/Icon'
import type { ColumnsSliceDefault, ColumnsSliceWithDividers, Content } from '@/content-types'
import { asText, isFilled } from '@/lib/content-fields'
import { ContentLink, RichText, type SliceComponentProps } from '@/lib/rich-text'
import { getBackgroundColor } from '@/utils/helpers'
import { linkResolver } from '@/utils/linkResolver'

export type ColumnsProps = SliceComponentProps<Content.ColumnsSlice>

const Columns: FC<ColumnsProps> = ({ slice }) => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={getBackgroundColor(slice.primary.background)}
    >
      {slice.variation === 'default' && <Default slice={slice} />}
      {slice.variation === 'withDividers' && <WithDividers slice={slice} />}
    </section>
  )
}

const Default = ({ slice }: { slice: ColumnsSliceDefault }) => (
  <div className="relative container overflow-visible py-10 md:pb-[63px]">
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{
        ease: cubicBezier(0.3, 0.3, 0.3, 0.4),
        duration: 1.25,
      }}
      className="grid grid-cols-1 gap-[38px] md:grid-cols-2 lg:grid-cols-3"
    >
      {slice.primary.column.map((column) => (
        <div key={asText(column.title)} className="relative pt-[52px]">
          {isFilled.select(column.icon) && (
            <div className="-top-10 left-0 flex h-[67px] w-[67px] shrink-0 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-lg md:flex lg:absolute">
              <IconComponent icon={column.icon} size="medium" className="text-secondary !size-8" />
            </div>
          )}

          {isFilled.richText(column.title) && (
            <RichText
              field={column.title}
              components={{
                heading5: ({ children }) => (
                  <h5 className="font-rubik mb-3 text-3xl tracking-tight">{children}</h5>
                ),
              }}
            />
          )}

          {isFilled.richText(column.description) && (
            <RichText
              field={column.description}
              components={{
                paragraph: ({ children }) => (
                  <p className="text-foreground/90 text-lg leading-relaxed">{children}</p>
                ),
              }}
            />
          )}
        </div>
      ))}
    </motion.div>
  </div>
)

const WithDividers = ({ slice }: { slice: ColumnsSliceWithDividers }) => (
  <div className="container">
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{
        ease: cubicBezier(0.3, 0.3, 0.3, 0.4),
        duration: 1.25,
      }}
      className="grid grid-cols-1 gap-16 md:grid-cols-2 lg:grid-cols-3"
    >
      {slice.primary.column.map((column) => (
        <div key={asText(column.title)} className="relative">
          {isFilled.richText(column.title) && (
            <RichText
              field={column.title}
              components={{
                heading4: ({ children }) => <h4 className="text-[22px]">{children}</h4>,
                heading5: ({ children }) => <h5 className="text-[22px]">{children}</h5>,
                heading6: ({ children }) => <h6 className="text-[22px]">{children}</h6>,
              }}
            />
          )}

          <hr className="border-primary my-5 w-[70px] border-b-3" />

          {isFilled.richText(column.description) && (
            <RichText
              field={column.description}
              components={{
                paragraph: ({ children }) => <p className="mb-3.5 text-lg">{children}</p>,
              }}
            />
          )}

          {isFilled.link(column.link) && (
            <ContentLink
              field={column.link}
              linkResolver={linkResolver}
              className="flex items-center gap-2 text-lg font-semibold"
            >
              <IconComponent
                icon="ChevronRight"
                size="xsmall"
                className="text-primary mt-0.5 !size-3 shrink-0"
              />
              {column.link.text}
            </ContentLink>
          )}
        </div>
      ))}
    </motion.div>
  </div>
)

export default Columns
